import { Languages } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type Locale,
} from "@/locales/routeExplanations";

/**
 * Compact segmented language picker shown in the Preferences sheet.
 *
 * Persists the choice via `useLocale` (localStorage-backed) so it survives
 * reloads and propagates to every consumer of `getRouteCopy()` /
 * `useLocale()` in the app.
 */
export function LocaleSwitcher() {
  const [locale, setLocale] = useLocale();
  return (
    <div>
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <Languages className="w-4 h-4" /> Language
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Used for route descriptions and turn-by-turn copy.
      </p>
      <div
        role="radiogroup"
        aria-label="Language"
        className="grid grid-cols-3 gap-2"
      >
        {SUPPORTED_LOCALES.map((l) => {
          const on = l === locale;
          return (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setLocale(l as Locale)}
              className={`h-10 rounded-full text-sm font-medium border transition-smooth ${
                on
                  ? "bg-pleasant text-primary-foreground border-transparent shadow-glow"
                  : "bg-card border-border hover:border-primary/40"
              }`}
            >
              {LOCALE_LABELS[l]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
